// When a result has stopped being ON ITS WAY and started being MISSING.
//
// Results arrive one way: the settle job runs at 07:00 Australia/Sydney every
// morning (server/cron/settle.mjs) and writes the night's scores into the
// closing day the app reads back. A refresh on the History screen can ask for
// the same thing early, but the morning run is the promise — so the honest
// test for "this should have a result by now" is not an elapsed number of
// hours, it is: HAS A SETTLE COME AND GONE SINCE THE MATCH FINISHED?
//
// That distinction is the whole file. A match that kicked off at 20:00 last
// night is not overdue at midnight, whatever a "six hours later" rule would
// say — nothing was ever going to look for its score until 07:00. The same
// match at 07:01 is overdue, because the one thing that fetches results has
// already been and found nothing.
//
// Pure, and a leaf: `localDate` comes from `lib/tickets.js`, which imports
// nothing at all, so this runs in a node test and can be read by the store,
// the paper trail and the history table alike without any of them dragging
// the others in.

import { localDate } from './tickets.js';
import { legKickoff } from './when.js';

/** The hour the scheduled settle runs, on the pad's own clock. */
export const SETTLE_HOUR_SYDNEY = 7;

/**
 * How long a match is assumed to take from kickoff to a final score.
 *
 * Four hours covers every code the app prices — an AFL game with a long break
 * runs about three, a Test-length game is not on the board — and it is
 * deliberately generous. Erring long only ever DELAYS the "No result" stamp by
 * one morning; erring short would put it on a match that is still being
 * played.
 */
export const MATCH_LENGTH_MS = 4 * 60 * 60 * 1000;

/**
 * How long a RACE takes, which is not four hours and is not close to it.
 *
 * Five minutes from the jump covers the run, the correct weight and the feed
 * getting there: a 1200m race is seventy seconds, and the result is published
 * within a minute or two of the last runner crossing. The generosity that is
 * right for a football match is wrong here — a card carries eight races an hour
 * at one venue, and a four-hour rule would still call the 2:40 pending at
 * 6:30 while the punter watched it win on the television.
 */
export const RACE_LENGTH_MS = 5 * 60 * 1000;

/**
 * What a race that HAS a result is still waiting for.
 *
 * PuntersEdge publishes placings as `interim` and holds them there until they
 * have not moved for thirty minutes — a protest, a photo or a late scratching
 * all move them — and this app settles off `final` only. Five minutes past the
 * jump that race is already "overdue" by the rule above, and "No result" would
 * be a lie: the result is in, it is just not the last word yet. This is the
 * sentence that says so, and it lives here because this file is where the app
 * keeps its opinions about a result that has not arrived.
 */
export const INTERIM_NOTE = 'Result in. Waiting for it to be made final';

/**
 * Racing sport ids are prefixed, so the test is the prefix.
 *
 * NOT imported from `engine/teams.js` (`RACING_SPORTS`, `isRacingSport`) on
 * purpose: this file's header calls it a leaf, and it is one — `lib/tickets.js`
 * imports nothing at all — where teams.js drags six team tables in behind it.
 * `results-due.test.js` walks `RACING_SPORTS` through `eventLengthFor` so the
 * two cannot drift.
 */
const isRacing = (sport) => String(sport ?? '').startsWith('racing_');

/**
 * How long to wait after this sport's event STARTS before a result is due.
 *
 * The only reason this takes a sport at all: everything the app priced until
 * racing landed took hours, and a race takes minutes.
 */
export function eventLengthFor(sport) {
  return isRacing(sport) ? RACE_LENGTH_MS : MATCH_LENGTH_MS;
}

const SYDNEY = 'Australia/Sydney';

/** A moment in milliseconds, from a Date, a number or an ISO string. Null if unreadable. */
function msOf(value) {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

// --- reading a wall clock in another zone --------------------------------------
//
// `localDate` answers "which calendar day is it over there", which is what the
// rest of the app needs and all it needs. This file also needs the reverse —
// "what instant is 07:00 over there" — and that cannot be had from a date
// string, because the offset it is asked at is the thing being looked up.
//
// One `Intl.DateTimeFormat` per zone does both jobs, cached because building
// one is the expensive part and this is called once per row on a screen that
// can hold a season of them. No new dependency: the same Intl the codebase
// already leans on for `localDate`.

const FORMATTERS = new Map();

function formatterFor(timeZone) {
  let fmt = FORMATTERS.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    FORMATTERS.set(timeZone, fmt);
  }
  return fmt;
}

/** What the wall clock in `timeZone` reads at `ms`, as plain numbers. */
function zonedParts(ms, timeZone) {
  const out = {};
  for (const { type, value } of formatterFor(timeZone).formatToParts(ms)) {
    if (type !== 'literal') out[type] = Number(value);
  }
  return out;
}

/**
 * The zone's offset from UTC at `ms`, in milliseconds.
 *
 * The wall clock read as if it were UTC, minus the instant it was read at. The
 * seconds are floored off both sides because `formatToParts` has no
 * milliseconds to give back, and an offset is always a whole number of minutes
 * anyway.
 */
function zoneOffsetMs(ms, timeZone) {
  const p = zonedParts(ms, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
}

/**
 * The instant at which the clock in `timeZone` reads `hour`:00 on a given
 * calendar day. `day` may be 0 or negative — `Date.UTC` rolls it back over the
 * month and the year, which is how "yesterday" is asked for below.
 *
 * TWO PASSES, because the offset depends on the answer. The first guess uses
 * the offset at the wall-clock time read as UTC, which is right except within
 * an offset's distance of a daylight-saving change; the second uses the offset
 * at that guess, which is the real one. 07:00 is never a time Sydney skips or
 * repeats — both switches happen at 02:00/03:00 — so the second pass always
 * lands.
 */
function zonedEpoch(year, month, day, hour, timeZone) {
  const wall = Date.UTC(year, month - 1, day, hour, 0, 0, 0);
  const guess = wall - zoneOffsetMs(wall, timeZone);
  return wall - zoneOffsetMs(guess, timeZone);
}

/**
 * The most recent `hour`:00 in `timeZone` at or before `now`, as epoch ms.
 *
 * At 06:59 Sydney that is yesterday morning's settle; at 07:00 it is this
 * morning's. `NaN` for a `now` that cannot be read, which makes every
 * comparison below false — nothing is called overdue on a broken clock.
 */
export function lastSettleAt(now = Date.now(), { hour = SETTLE_HOUR_SYDNEY, timeZone = SYDNEY } = {}) {
  const ms = msOf(now);
  if (ms === null) return NaN;
  const [year, month, day] = localDate(ms, timeZone).split('-').map(Number);
  const today = zonedEpoch(year, month, day, hour, timeZone);
  return today <= ms ? today : zonedEpoch(year, month, day - 1, hour, timeZone);
}

/**
 * Should this match have a result by now? True when it finished before the
 * last scheduled settle — which is to say a settle has already looked for its
 * score and come back without one.
 *
 * False for a kickoff that cannot be read. A leg with no time on it is not
 * evidence of anything, and a row that stamped "No result" on a parse failure
 * would be the app blaming the bookie for its own bad record.
 *
 * RACING IS THE ONE SPORT THAT IS NOT MEASURED AGAINST THE MORNING SETTLE, and
 * the whole of the reason is at the top of this file: the test is "has the one
 * thing that fetches results been and gone", and for a race that thing is not
 * the 07:00 job. `syncSettlement` asks `/api/racing/results` for the day itself,
 * minutes after the jump, on the punter's own key — so five minutes past the
 * off IS a fetch that has been and come back without one, and waiting until
 * seven the next morning to say so would be the app pretending it had not
 * looked. `opts.sport` is how a caller says which rule it is under; a leg that
 * does not say is a match, as every leg was before racing landed.
 */
export function resultOverdue(kickoff, now = Date.now(), opts) {
  const at = msOf(kickoff);
  if (at === null) return false;
  const finished = at + eventLengthFor(opts?.sport);
  if (!isRacing(opts?.sport)) return finished < lastSettleAt(now, opts);
  const ms = msOf(now);
  return ms !== null && finished < ms;
}

// `legKickoff` — when a leg says it starts, either shape it comes in — moved
// to `lib/when.js`, which needs it too and is the leaf both this file and the
// components import from. Re-exported here so nothing that already imports it
// from this file has to change.
export { legKickoff };

/**
 * What a leg's sport is, for the two rules above. The leg's own id first; the
 * matchKey is the fallback, and it is a good one — every key this app builds
 * starts with the sport (`racing_horse:2026-09-15:randwick:R5`), so a leg that
 * lost its `sport` field in a migration still knows what it is.
 */
export const legSportOf = (leg) => leg?.sport ?? String(leg?.matchKey ?? '').split(':')[0] ?? null;

/**
 * When a leg's event actually STARTED, which for a race is not always when it
 * was advertised to.
 *
 * `inplayAt` is the observed off and `kickoff` is only the card's promise. A
 * race that jumps four minutes late and is called overdue off the advertised
 * time would be stamped "No result" while it was still being run. Every other
 * sport carries no `inplayAt` at all and is unaffected.
 */
export const legEventStart = (leg) => leg?.inplayAt ?? legKickoff(leg);

/**
 * Is this whole ticket waiting on a result that should already have arrived?
 *
 * Every leg STILL UNRESOLVED must be overdue, and there must be at least one.
 * Legs already marked are ignored, not disqualifying: the ticket that raised
 * this was an MLB + NRL multi where the NRL leg settled overnight and the MLB
 * legs never did — results came back for half of it, and the half that is
 * holding it up is exactly the "no score came back" the stamp is for. The note
 * in the panel says to tap a leg, and the unmarked legs are the ones to tap.
 * A ticket with no readable kickoff on any unresolved leg answers false,
 * because there is nothing here that says the match is over.
 */
export function legsOverdue(legs, now = Date.now(), opts) {
  const list = Array.isArray(legs) ? legs : [];
  let waiting = 0;
  for (const leg of list) {
    if (leg?.result != null) continue;
    const kickoff = legEventStart(leg);
    if (!kickoff) continue;
    waiting += 1;
    if (!resultOverdue(kickoff, now, { ...opts, sport: legSportOf(leg) })) return false;
  }
  return waiting > 0;
}
